import { ActionEnum, Node, Role, Uni } from '@vendia/management-api-types'
import debug from 'debug'
import { VendiaResourceRepresentation } from 'src/types/types'
import { rolePermitsDataActionVendiaResource } from 'src/utils/rbac/role-vendia-resource'

import useGetCurrentVendiaUserQuery from '../hooks/use-current-vendia-user-query'
import { useSelectedRole } from '../hooks/use-selected-role'

// Not namespaced under "app:" because it's super noisy and not generally useful when
// using localStorage.debug = 'app:*' to see majority of app logs
const logger = debug('node-access')

export enum AccessLevel {
  READ = 'READ',
  ALL = 'ALL',
  NONE = 'NONE',
}

export const useNodeAccessLevels = (uni?: Uni): Map<string, AccessLevel> | undefined => {
  const { getCurrentVendiaUserQuery } = useGetCurrentVendiaUserQuery()
  const user = getCurrentVendiaUserQuery?.data?.getUser
  const role = useSelectedRole()
  const userEmail = user?.email
  const userId = user?.userId
  if (!uni || !role || !userEmail) {
    return
  }
  return uni?.nodes?.reduce((previous, node) => {
    const accessLevel = getNodeAccessLevel(uni, role, node, userEmail, userId)
    previous.set(node.name, accessLevel)
    return previous
  }, new Map<string, AccessLevel>())
}

const wildcardTest = (wildcard: string, testString: string) => {
  const w = wildcard.replace(/[.+^${}()|[\]\\]/g, '\\$&') // regexp escape
  const re = new RegExp(`^${w.replace(/\*/g, '.*').replace(/\?/g, '.')}$`, 'i')
  return re.test(testString)
}

export const isNodeOwner = (node?: Node, userEmail?: string, userId?: string) => {
  return (
    node?.userId?.toLowerCase() === userEmail?.toLowerCase() || node?.userId?.toLowerCase() === userId?.toLowerCase()
  )
}

export const hasReadAccess = (nodeAccessMap: Map<string, AccessLevel>, node: Node) => {
  return [AccessLevel.ALL, AccessLevel.READ].some((accessLevel) => nodeAccessMap.get(node.name) === accessLevel)
}

export const getNodeAccessLevel = (
  uni: Uni,
  role: Role,
  node: Node,
  userEmail: string,
  userId?: string,
): AccessLevel => {
  if (!node?.orgId || !node?.name || !uni?.name) {
    logger('Missing orgId, name, or uni name for node', node?.orgId, node?.name, uni?.name)
    return AccessLevel.NONE
  }

  // Data plane RBAC check
  const vendiaResource: VendiaResourceRepresentation = `VendiaResource(${node.orgId}/${uni.name}/${node.name})`

  if (
    rolePermitsDataActionVendiaResource({
      role,
      action: ActionEnum.DataAll,
      resource: vendiaResource,
      enforceEntityAndField: false,
    })
  ) {
    logger('User has access to node via RBAC', node.name)
    return AccessLevel.ALL
  }

  if (
    rolePermitsDataActionVendiaResource({
      role,
      action: ActionEnum.DataRead,
      resource: vendiaResource,
      enforceEntityAndField: false,
    })
  ) {
    logger('User has access to node via RBAC', node.name)
    return AccessLevel.READ
  }

  // Legacy node owner check - TODO: remove when we're migrated to RBAC for data plane access
  if (isNodeOwner(node, userEmail, userId)) {
    logger('User has access as node owner (legacy)', node.name)
    return AccessLevel.ALL
  }

  // Legacy allow list check - TODO: remove when we're migrated to RBAC for data plane access
  if (node?.settings?.apiSettings?.auth?.allowList && node?.settings?.apiSettings?.auth?.allowList?.length > 0) {
    if (
      node.settings.apiSettings.auth.allowList.map((value) => value?.toLowerCase()).includes(userEmail?.toLowerCase())
    ) {
      // User is explicitly allowed in the allow list
      logger('User has access via allow list (legacy)', node.name)
      return AccessLevel.ALL
    }
    if (
      node.settings.apiSettings.auth.allowList.some((entry) => {
        if (entry?.includes('*')) {
          const wildcardPrefix = entry.split('@')[0]
          const wildcardDomain = entry.split('@')[1]
          const userEmailPrefix = userEmail.split('@')[0]
          const userEmailDomain = userEmail.split('@')[1]
          // The allow list contains a wildcard, so check if the user's email matches the wildcard
          return (
            wildcardTest(wildcardPrefix, userEmailPrefix) &&
            wildcardDomain?.toLowerCase() === userEmailDomain?.toLowerCase()
          )
        }
        return false
      })
    ) {
      logger('User has access to node via allow list (legacy)', node.name)
      return AccessLevel.ALL
    }
  }
  logger('User has no access to node', node.name)
  return AccessLevel.NONE
}
